/**
 * Sauvegarder et reprendre une partie.
 *
 * C'est le contrôle qui met à l'épreuve la promesse structurelle du § 3.2 : **aucune
 * information de jeu ne vit en dehors de l'état**. La v1 échouait précisément là —
 * `rampeau`, `turnThrows` et `memResolved` étaient des variables de module,
 * reconstruites au rechargement, donc perdues. Ici, si la reprise en plein jet ou en
 * plein rampeau ne redonne pas exactement la même partie, c'est que quelque chose vit
 * ailleurs que dans `Partie`, et il faut le corriger là plutôt qu'ici.
 *
 * ## Ce que ce module ne fait pas
 *
 * Il ne choisit pas où écrire. `localStorage` est **interdit** pour la sauvegarde
 * (§ 5.3) : l'OS peut le purger sans avertir. Le stockage passera par
 * `@capacitor/preferences` côté natif ; ce module ne rend qu'une chaîne, et c'est
 * l'appelant qui décide de sa destination.
 */
/**
 * Version du format. À incrémenter dès que la forme de `Partie` change de façon
 * incompatible : mieux vaut refuser une sauvegarde ancienne que reprendre une partie
 * à moitié comprise.
 */
export const VERSION_SAUVEGARDE = 1;
export class SauvegardeIllisible extends Error {
}
/** Sérialise une partie. Le résultat est du JSON, sans dépendance à l'environnement. */
export function serialiser(partie) {
    const enveloppe = {
        version: VERSION_SAUVEGARDE,
        ruleSetId: partie.regles.id,
        partie,
    };
    return JSON.stringify(enveloppe);
}
/**
 * Relit une partie sauvegardée.
 *
 * Refuse plutôt que de deviner : une sauvegarde d'une autre version de format ou
 * d'un autre RuleSet donnerait une partie jouable en apparence et fausse en pratique
 * — les tables d'IA et la hiérarchie des combinaisons ne seraient plus les mêmes.
 */
export function deserialiser(brut, ruleSetId) {
    let enveloppe;
    try {
        enveloppe = JSON.parse(brut);
    }
    catch {
        throw new SauvegardeIllisible("sauvegarde illisible : JSON invalide");
    }
    if (!enveloppe || typeof enveloppe !== "object") {
        throw new SauvegardeIllisible("sauvegarde illisible : enveloppe absente");
    }
    if (enveloppe.version !== VERSION_SAUVEGARDE) {
        throw new SauvegardeIllisible(`sauvegarde en version ${String(enveloppe.version)}, `
            + `attendue ${VERSION_SAUVEGARDE}`);
    }
    if (ruleSetId !== undefined && enveloppe.ruleSetId !== ruleSetId) {
        throw new SauvegardeIllisible(`sauvegarde pour les règles « ${enveloppe.ruleSetId} », `
            + `en vigueur « ${ruleSetId} »`);
    }
    const partie = enveloppe.partie;
    verifierForme(partie);
    return partie;
}
/**
 * Contrôles de forme minimaux.
 *
 * Volontairement structurels et non « intelligents » : on vérifie que les champs dont
 * dépend le moteur existent, pas que la partie soit cohérente. Une partie incohérente
 * mais bien formée sera refusée par le moteur à la première action, avec un message
 * bien plus précis que tout ce qu'on pourrait dire ici.
 */
function verifierForme(partie) {
    const p = partie;
    if (!p || typeof p !== "object") {
        throw new SauvegardeIllisible("partie absente de la sauvegarde");
    }
    const manques = [];
    if (typeof p.revision !== "number")
        manques.push("revision");
    if (!p.regles || typeof p.regles.id !== "string")
        manques.push("regles");
    if (typeof p.phase !== "string")
        manques.push("phase");
    if (typeof p.masse !== "number")
        manques.push("masse");
    if (!Array.isArray(p.joueurs))
        manques.push("joueurs");
    if (!Array.isArray(p.actifs))
        manques.push("actifs");
    if (!Array.isArray(p.classement))
        manques.push("classement");
    if (manques.length > 0) {
        throw new SauvegardeIllisible(`champs manquants : ${manques.join(", ")}`);
    }
    if (p.joueurs.length < 2) {
        throw new SauvegardeIllisible(`${p.joueurs.length} joueur(s) dans la sauvegarde`);
    }
}
/**
 * Vrai si les deux parties sont identiques au sens de la reprise.
 *
 * Les **événements** sont exclus de la comparaison, et c'est volontaire : ils sont
 * décoratifs et bornés (§ 3.2), une reprise n'a pas à les retrouver. Tout le reste
 * doit coïncider — c'est ce qui donne un sens à « la partie est intacte ».
 */
export function memePartie(a, b) {
    return JSON.stringify(sansEvenements(a)) === JSON.stringify(sansEvenements(b));
}
function sansEvenements(p) {
    const { evenements: _ignores, ...reste } = p;
    return reste;
}
//# sourceMappingURL=sauvegarde.js.map